// This is entry point for all tvpil modules that will be used in rhino.

import {
  tvpilSetCacheService,
  assetToType,
  addKsql,
  KuXDms,
  MediaType,
  getRatioByType,
  categoryTreeItemsMap,
  imageTypes,
  ProviderSettings,
  generateMenuLink,
  UIDAPresentationTypeEnum,
  TypeOfApplicationLink,
  getTypeByTypeName,
} from '@kaltura-ott/tvpil';
import { container } from 'tsyringe';

import type { AppStore } from '../../../stores/app/app-store';
import { isNewSubtypeSupported } from '../../../stores/category/widgets/rails/rail-store-config';
import type {
  LastWatchedIdEntry,
  LastWatchedLinearStore,
} from '../../../stores/channelStats/last-watched-linear-store';
import { creatorEntityStore } from '../../../stores/entities/entities.service';
import type { FavoriteRepository } from '../../../stores/favoriteRepository/favorite-repository';
import type { LinearRepository } from '../../../stores/linearRepository/linear-repository';
import { type ProvidersRepository } from '../../../stores/providersRepository/providers-repository';
import type { TransactionStore } from '../../../stores/transaction/transaction-store';

export type LastWatchedEntry = LastWatchedIdEntry;

export type {
  TypeOfApplicationLink,
  AdditionalData,
  KuxFilteredCategoriesDTO,
  CategoryUidaAsset,
} from '@kaltura-ott/tvpil';
export type { ChannelStatsCache, ChannelStats } from '@kaltura-ott/tvpil';
export {
  LCBatchesPresentationOrderEnum,
  KuxRating,
  MediaType,
  UIDAPresentationTypeEnum,
  AssetPresentationRatioEnum,
  SecondButtonActions,
} from '@kaltura-ott/tvpil';

const containerCache = new Map<string, unknown>();

function getContainerWithCache<T>(token: string): T {
  if (containerCache.has(token)) {
    return containerCache.get(token) as T;
  }

  const cont = container.resolve<T>(token) as T;
  containerCache.set(token, cont);

  return cont;
}

export const tvpil = {
  get addKsql() {
    return addKsql;
  },

  get appStore(): AppStore {
    return getContainerWithCache<AppStore>('appStore');
  },

  assetToType,

  get availableRatings() {
    return this.appStore.dms.ratingMap;
  },

  get availableRatingsNames() {
    return this.availableRatings.map((r) => r.name);
  },

  creatorEntityStore,
  getRatioByType,
  getTypeByTypeName,

  get dms() {
    return KuXDms;
  },

  setCacheService: tvpilSetCacheService,
  categoryTreeItemsMap: categoryTreeItemsMap,

  get linearRepository() {
    return getContainerWithCache<LinearRepository>('linearRepository');
  },

  get primeTime() {
    return this.appStore.primeTime;
  },

  get providersRepository() {
    return getContainerWithCache<ProvidersRepository>('providersRepository');
  },

  get lastWatchedLinearStore() {
    return getContainerWithCache<LastWatchedLinearStore>('lastWatchedLinearStore');
  },

  get providers(): ProviderSettings {
    return this.dms.providers;
  },

  get availableProviders(): string[] {
    return Object.values(this.providers).map((value) => value.metaKey);
  },

  get transactionStore() {
    return getContainerWithCache<TransactionStore>('transactionStore');
  },

  get imageTypes() {
    return imageTypes;
  },

  generateMenuLink(
    linkType: TypeOfApplicationLink,
    inAppLink: string,
    targetId?: number | string,
    presentationType?: UIDAPresentationTypeEnum,
  ) {
    return generateMenuLink(linkType, inAppLink, targetId, presentationType);
  },

  get favoritesTtl() {
    return getContainerWithCache<FavoriteRepository>('favoriteRepository').ttl;
  },

  isNewSubtypeSupported(subtype: UIDAPresentationTypeEnum) {
    return isNewSubtypeSupported(subtype);
  },
  get userId() {
    return this.appStore.userStore.userData?.id;
  },

  get cacheService() {
    return this.appStore.params.cacheService;
  },

  get rootStore() {
    return this.appStore?.rootStore;
  },

  getDetailsImageProperties(mediaType: MediaType) {
    const assetDetailsPages = this.appStore?.rootStore?.assetDetailsPages;

    return assetDetailsPages.getImageProperties(mediaType);
  },

  syncFavoriteRepository() {
    return getContainerWithCache<FavoriteRepository>('favoriteRepository').syncLocalState();
  },
};
